const SESSION_KEY = 'loom_token';
const USER_KEY = 'loom_user';
const AI_TOKEN_KEY = 'loom_ai_token';
const ONBOARD_DONE_KEY = 'loom_onboard_done';
const ONBOARD_STEP_KEY = 'loom_onboard_step';
const ONBOARD_PROMPTED_KEY = 'loom_onboard_prompted';

export function apiOrigin(): string {
  const raw = import.meta.env.VITE_API_URL;
  return typeof raw === 'string' ? raw.replace(/\/$/, '') : '';
}

export function loginUrl(): string {
  return `${apiOrigin()}/auth/portal/login`;
}

export function getSessionToken(): string | null {
  try {
    return localStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
}

export function getAiToken(): string | null {
  try {
    return localStorage.getItem(AI_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setAiToken(token: string): void {
  try {
    localStorage.setItem(AI_TOKEN_KEY, token);
  } catch {
    /* private mode */
  }
}

export function setSession(token: string, name?: string | null, role?: string | null): void {
  try {
    localStorage.setItem(SESSION_KEY, token);
    if (name || role) localStorage.setItem(USER_KEY, JSON.stringify({ name, role }));
  } catch {
    /* private mode */
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(AI_TOKEN_KEY);
  } catch {
    /* private mode */
  }
}

export function markOnboardingDone(): void {
  try {
    localStorage.setItem(ONBOARD_DONE_KEY, '1');
  } catch {
    /* private mode */
  }
}

export function isOnboardingDone(): boolean {
  try {
    return localStorage.getItem(ONBOARD_DONE_KEY) === '1';
  } catch {
    return false;
  }
}

export function getOnboardingStep(): number {
  try {
    const n = parseInt(localStorage.getItem(ONBOARD_STEP_KEY) || '0', 10);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

export function setOnboardingStep(n: number, total: number): void {
  try {
    localStorage.setItem(ONBOARD_STEP_KEY, String(n));
    if (n === total - 1) localStorage.setItem(ONBOARD_DONE_KEY, '1');
  } catch {
    /* private mode */
  }
}

interface JwtPayload {
  sub?: string;
  exp?: number;
  iat?: number;
  name?: string;
  role?: string;
}

export function decodeJwt(token: string): JwtPayload | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

export function daysUntilExpiry(token: string): number | null {
  const payload = decodeJwt(token);
  if (!payload?.exp) return null;
  return Math.floor((payload.exp * 1000 - Date.now()) / 86400000);
}

function tokenNeedsRefresh(existing: string): boolean {
  const payload = decodeJwt(existing);
  if (!payload?.exp) return true;
  return (payload.exp * 1000 - Date.now()) / 86400000 <= 3;
}

/**
 * Authentik returns the portal JWT in the URL hash. Capture it once, then strip the hash so it
 * is not left in history.
 */
export function captureAuthHash(): void {
  const hash = new URLSearchParams(window.location.hash.slice(1));
  const token = hash.get('token');
  if (!token) return;
  const existing = getSessionToken();
  if (!existing || tokenNeedsRefresh(existing)) {
    setSession(token, hash.get('name'), hash.get('role'));
  } else {
    setSession(existing, hash.get('name'), hash.get('role'));
  }
  history.replaceState(null, '', window.location.pathname + window.location.search);
}

export function shouldPromptOnboarding(token: string): boolean {
  try {
    if (localStorage.getItem(ONBOARD_DONE_KEY)) return false;
    if (sessionStorage.getItem(ONBOARD_PROMPTED_KEY)) return false;
    sessionStorage.setItem(ONBOARD_PROMPTED_KEY, '1');
    const payload = decodeJwt(token);
    const ageMin = (Date.now() / 1000 - (payload?.iat ?? 0)) / 60;
    return ageMin < 30;
  } catch {
    return false;
  }
}

export function currentUserId(token: string | null = getSessionToken()): string | null {
  if (!token) return null;
  return decodeJwt(token)?.sub ?? null;
}
